/**
 * Where the phone bar's transcript comes to rest after it changes.
 *
 * ── Why this exists (MUCKA-WEB-CZ) ──────────────────────────────────────────
 *
 * The transcript is a short box (190px by default) pinned to its newest line.
 * An approval card hangs off the reply that was last when it arrived, and on a
 * call the conversation carries on underneath it, so two more turns push the
 * card's Send button out of the top of the box with nothing to say it is there.
 * On 12 Oct 2026 an owner on the iPhone app was asking Mucka to text a batch of
 * customers; the probe found the first card's Send button 300px above the box
 * (button at y 241–286, box at 542–732). He said "send them, not stage them",
 * and Mucka approved it out loud. He never had a button he could see.
 *
 * So during a call, while a card is waiting for a tap, the pin stops at that
 * card: its Send row sits at the foot of the box, and anything newer is below
 * it. What Mucka says after the card is spoken anyway; the card is the one
 * thing on the thread that only works if it is seen. Once it is answered the
 * pin goes back to the newest line. Typed chat keeps the plain pin, because
 * there the newest reply is something to read.
 *
 * Pure and directive-free: MobileNav and its tests both read it.
 */
import type { StagedState } from '@/components/chat/BubbleBlock';

/** The decisions that close a card. Anything else still needs the owner. */
const ANSWERED: ReadonlySet<StagedState> = new Set(['approved', 'dismissed']);

/**
 * The newest card in the thread that is drawn and not yet answered, or null.
 *
 * `drawnAt` is where each id is allowed to draw (`latestStagedIndex`), so a
 * card rewritten in place is counted at its live copy only. `skip` leaves out
 * cards drawn some other way (a document card carries its own Send).
 */
export function waitingCardId(
  messages: readonly { stagedActions?: readonly { id: string }[] }[],
  drawnAt: ReadonlyMap<string, number>,
  states: Readonly<Record<string, StagedState | undefined>>,
  skip: (messageIndex: number, id: string) => boolean = () => false,
): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const cards = messages[i].stagedActions ?? [];
    for (let j = cards.length - 1; j >= 0; j--) {
      const id = cards[j].id;
      if (drawnAt.get(id) !== i || skip(i, id)) continue;
      if (!ANSWERED.has(states[id] ?? 'idle')) return id;
    }
  }
  return null;
}

/**
 * The `scrollTop` the transcript should rest at.
 *
 * With nothing held, the newest line, as it always was. Holding a card, as far
 * down as still keeps its Send row in the box — which is the newest line too
 * whenever the card is the last thing in it. Measured from the Send row, not
 * the card's text, for the reason `stagedCardPlacement` gives: a long message
 * can show its first line while the button is out of view. A copy that has
 * not been laid out falls back to the newest line rather than guessing.
 */
export function transcriptRestingTop(scroller: HTMLElement, holdId: string | null): number {
  const newest = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  if (!holdId) return newest;
  const copy = Array.from(scroller.querySelectorAll<HTMLElement>('[data-staged-id]'))
    .find(el => el.dataset.stagedId === holdId);
  if (!copy) return newest;
  const row = (copy.nextElementSibling as HTMLElement | null) ?? copy;
  const r = row.getBoundingClientRect();
  if (r.height <= 0) return newest;
  const rowBottom = r.bottom - scroller.getBoundingClientRect().top + scroller.scrollTop;
  return Math.min(newest, Math.max(0, rowBottom - scroller.clientHeight));
}
